import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useOrganizationContext } from '@/contexts/OrganizationContext'

export interface KbChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  actions?: KbActionLog[]
  ts: string
}

export interface KbActionLog {
  type: string
  detail: Record<string, unknown>
}

const WRITE_ACTION_TYPES = new Set([
  'patch_briefing_field',
  'patch_prompt_field',
  'update_flow_prompt',
  'add_prompt_rule',
  'patch_document_chunks',
  'manage_knowledge_url',
  'generate_agent',
  'create_knowledge_document',
  'manage_api',
  'rate_conversation',
])

type OnWriteCallback = (actions: KbActionLog[]) => void

interface KbAgentChatContextValue {
  messages: KbChatMessage[]
  sending: boolean
  sessionId: string | null
  sendMessage: (text: string) => Promise<void>
  clearSession: () => void
  /** Registra um callback chamado quando o agente executa qualquer ação de escrita */
  onWrite: (cb: OnWriteCallback) => () => void}

const KbAgentChatContext = createContext<KbAgentChatContextValue | null>(null)

export function KbAgentChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<KbChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const { user } = useAuth()
  const { currentOrg } = useOrganizationContext()

  // Registry of write-event listeners
  const writeListeners = useRef<Set<OnWriteCallback>>(new Set())

  const onWrite = useCallback((cb: OnWriteCallback) => {
    writeListeners.current.add(cb)
    return () => writeListeners.current.delete(cb)
  }, [])

  const notifyWrite = useCallback((actions: KbActionLog[]) => {
    writeListeners.current.forEach((cb) => cb(actions))
  }, [])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || sending) return

    const userMsg: KbChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      ts: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMsg])

    if (!currentOrg?.id) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: 'Organização não carregada. Recarregue a página e tente novamente.',
          ts: new Date().toISOString(),
        },
      ])
      return
    }

    setSending(true)

    try {
      const { data, error } = await supabase.functions.invoke('kb-agent-chat', {
        body: {
          org_id: currentOrg.id,
          session_id: sessionId,
          message: text.trim(),
          user_id: user?.id,
        },
      })

      if (error) throw error

      if (data?.session_id && !sessionId) {
        setSessionId(data.session_id)
      }

      const actions: KbActionLog[] = data?.actions ?? []
      const assistantMsg: KbChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data?.reply ?? 'Sem resposta.',
        actions,
        ts: new Date().toISOString(),
      }
      setMessages((prev) => [...prev, assistantMsg])

      if (actions.some((a) => WRITE_ACTION_TYPES.has(a.type))) {
        notifyWrite(actions)
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('[KbAgentChat] erro:', err)
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `Erro ao processar sua mensagem: ${detail}`,
          ts: new Date().toISOString(),
        },
      ])
    } finally {
      setSending(false)
    }
  }, [currentOrg?.id, notifyWrite, sending, sessionId, user?.id])

  const clearSession = useCallback(() => {
    setMessages([])
    setSessionId(null)
  }, [])

  return (
    <KbAgentChatContext.Provider value={{ messages, sending, sessionId, sendMessage, clearSession, onWrite }}>
      {children}
    </KbAgentChatContext.Provider>
  )
}

export function useKbAgentChatContext() {
  const ctx = useContext(KbAgentChatContext)
  if (!ctx) throw new Error('useKbAgentChatContext must be used inside KbAgentChatProvider')
  return ctx
}
